import { hasOwnProperty, safeIdCompare } from './utils';
import { Translated, ContentTemplateTranslation } from '../../shared/types/translation';
import {
  BareContentTemplate,
  BareContentTemplateLegacy,
  BasicContentTemplate,
  ContentTemplate,
} from '../../shared/types/contentTemplate';
import { Property } from '../../shared/types/property';
import { ResourceNotFoundError } from '../../shared/errors';
import {
  Brand,
  BrandContentSegment,
  BrandContentLevel,
  BrandContentAttribute,
} from '../../shared/types/brand';
import { searchById } from './functional';

export function translateBareContentTemplate(
  contentTemplate: BareContentTemplate,
  translation?: ContentTemplateTranslation,
  lang?: string
): Translated<BareContentTemplate> | undefined {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...contentTemplate,
    content: transforms[lang].delta.content,
    ...(isNotBasicContentTemplate(contentTemplate)
      ? { name: transforms[lang].delta.name, description: transforms[lang].delta.description }
      : {}),
    _lang: lang,
  };
}

export function convertContentTemplateToLegacy(
  contentTemplate: BareContentTemplate,
  property: Property
): BareContentTemplateLegacy {
  const { source: _, brand: __, properties: ___, ...rest } = contentTemplate;

  return {
    ...rest,
    property: property._id,
  };
}

export function convertLegacyContentTemplateToReal(
  contentTemplates: BareContentTemplate[],
  legacy: BareContentTemplateLegacy
): BareContentTemplate | undefined {
  const result = contentTemplates.find(ct => safeIdCompare(ct._id, legacy._id));
  return result;
}

export function isNotBasicContentTemplate(
  contentTemplate: BareContentTemplate | ContentTemplate
): contentTemplate is Exclude<BareContentTemplate, BasicContentTemplate> {
  return contentTemplate.type !== 'basic' && contentTemplate.type !== undefined;
}

/**
 * Given a bare event template and a brand, populate the reference fields from
 * the brand values
 *
 */
export function populateContentTemplate(
  bareTemplate: BareContentTemplate,
  brand: Brand
): ContentTemplate {
  // Ensure all referenced values exist, otherwise throw an error
  let segment: BrandContentSegment | undefined;
  if (bareTemplate.segment) {
    const match = searchById(bareTemplate.segment, brand.contentSegments);
    if (!match) {
      throw new ResourceNotFoundError(`Cannot find referenced segment ${bareTemplate.segment}`);
    }

    segment = match;
  }
  let levels: BrandContentLevel[] = [];
  bareTemplate.levels.forEach(levelId => {
    let level = searchById(levelId, brand.contentLevels);
    if (!level) throw new ResourceNotFoundError(`Cannot find referenced level ${levelId}`);
    levels.push(level);
  });
  let attributes: BrandContentAttribute[] = [];
  bareTemplate.attributes.forEach(attributeId => {
    let attribute = searchById(attributeId, brand.contentAttributes);
    if (!attribute) {
      throw new ResourceNotFoundError(`Cannot find referenced attribute ${attributeId}`);
    }
    attributes.push(attribute);
  });

  return {
    ...bareTemplate,
    segment,
    levels,
    attributes,
    brand,
  };
}